(() => {
  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  };

  const toArray = (value) => Array.from(value || []);
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const initRevealAnimations = () => {
    const targets = toArray(document.querySelectorAll('[style*="opacity: 0"]')).filter(
      (target) => !target.hasAttribute('data-no-reveal')
    );
    if (!targets.length) return;

    const reveal = (element) => {
      element.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      requestAnimationFrame(() => {
        element.style.opacity = '1';
        element.style.transform = 'none';
      });
    };

    if (!('IntersectionObserver' in window)) {
      targets.forEach(reveal);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -10% 0px',
      }
    );

    targets.forEach((target) => observer.observe(target));
  };

  const initGallery = (touchArea) => {
    const frame = touchArea.querySelector(':scope > div');
    if (!frame) return;

    const images = toArray(frame.querySelectorAll(':scope > img'));
    if (images.length < 2) return;

    const prevButton = touchArea.querySelector('button[aria-label="Previous image"]');
    const nextButton = touchArea.querySelector('button[aria-label="Next image"]');
    const dots = toArray(touchArea.querySelectorAll(':scope > div span'));

    let thumbs = [];
    const sibling = touchArea.nextElementSibling;
    if (sibling) {
      const maybeThumbs = toArray(sibling.querySelectorAll('button'));
      if (maybeThumbs.length && maybeThumbs.every((button) => button.querySelector('img'))) {
        thumbs = maybeThumbs;
      }
    }

    const normalize = (index) => {
      if (index < 0) return images.length - 1;
      if (index >= images.length) return 0;
      return index;
    };

    let currentIndex = images.findIndex((image) => image.classList.contains('opacity-100'));
    if (currentIndex < 0) currentIndex = 0;
    let suppressFrameClick = false;

    const hasThumbs = thumbs.length > 0;
    const lightboxSources = images.map((image) => image.currentSrc || image.getAttribute('src') || '');
    let lightboxRoot = null;
    let lightboxPanel = null;
    let lightboxImage = null;
    let lightboxDots = [];
    let lightboxOpen = false;
    let previousBodyOverflow = '';
    let lightboxTouchStartX = 0;
    let lightboxCloseTimeout = 0;
    const prefersReducedMotion =
      'matchMedia' in window && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const lightboxDurationMs = prefersReducedMotion ? 0 : 260;

    const updateLightbox = () => {
      if (!lightboxImage) return;

      const activeImage = images[currentIndex];
      lightboxImage.src =
        lightboxSources[currentIndex] || activeImage?.currentSrc || activeImage?.getAttribute('src') || '';

      lightboxDots.forEach((dot, index) => {
        dot.style.background = index === currentIndex ? '#ffffff' : 'rgba(255, 255, 255, 0.4)';
      });
    };

    const closeLightbox = () => {
      if (!lightboxOpen || !lightboxRoot) return;
      lightboxOpen = false;

      const finishClose = () => {
        if (!lightboxRoot || lightboxOpen) return;
        lightboxRoot.style.display = 'none';
        document.body.style.overflow = previousBodyOverflow;
      };

      lightboxRoot.style.opacity = '0';
      lightboxRoot.style.pointerEvents = 'none';
      if (lightboxPanel) {
        lightboxPanel.style.opacity = '0';
        lightboxPanel.style.transform = 'translateY(14px) scale(0.985)';
      }

      if (lightboxCloseTimeout) {
        window.clearTimeout(lightboxCloseTimeout);
        lightboxCloseTimeout = 0;
      }

      if (!lightboxDurationMs) {
        finishClose();
        return;
      }

      lightboxCloseTimeout = window.setTimeout(() => {
        lightboxCloseTimeout = 0;
        finishClose();
      }, lightboxDurationMs);
    };

    const openLightbox = () => {
      if (!hasThumbs) return;
      if (!lightboxRoot) return;
      if (lightboxOpen) return;

      lightboxOpen = true;
      if (lightboxCloseTimeout) {
        window.clearTimeout(lightboxCloseTimeout);
        lightboxCloseTimeout = 0;
      }
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      lightboxRoot.style.display = 'flex';
      updateLightbox();

      requestAnimationFrame(() => {
        if (!lightboxRoot || !lightboxOpen) return;
        lightboxRoot.style.opacity = '1';
        lightboxRoot.style.pointerEvents = 'auto';
        if (lightboxPanel) {
          lightboxPanel.style.opacity = '1';
          lightboxPanel.style.transform = 'translateY(0) scale(1)';
        }
      });
    };

    const buildLightbox = () => {
      if (!hasThumbs || lightboxRoot) return;

      lightboxRoot = document.createElement('div');
      lightboxRoot.style.cssText =
        'position:fixed;inset:0;z-index:80;background:rgba(0,0,0,0.95);display:none;align-items:center;justify-content:center;opacity:0;pointer-events:none;';
      lightboxRoot.style.transition = prefersReducedMotion
        ? 'none'
        : 'opacity 0.26s cubic-bezier(0.22, 1, 0.36, 1)';

      lightboxPanel = document.createElement('div');
      lightboxPanel.style.cssText =
        'position:relative;width:100%;height:100%;display:flex;align-items:center;justify-content:center;opacity:0;transform:translateY(14px) scale(0.985);';
      lightboxPanel.style.transition = prefersReducedMotion
        ? 'none'
        : 'opacity 0.26s cubic-bezier(0.22, 1, 0.36, 1), transform 0.26s cubic-bezier(0.22, 1, 0.36, 1)';

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.setAttribute('aria-label', 'Close lightbox');
      closeButton.textContent = '×';
      closeButton.style.cssText =
        'position:absolute;top:12px;right:12px;min-width:44px;min-height:44px;border:0;background:none;color:rgba(255,255,255,0.85);font-size:34px;line-height:1;cursor:pointer;';

      const prevLightboxButton = document.createElement('button');
      prevLightboxButton.type = 'button';
      prevLightboxButton.setAttribute('aria-label', 'Previous image');
      prevLightboxButton.textContent = '‹';
      prevLightboxButton.style.cssText =
        'position:absolute;left:8px;top:50%;transform:translateY(-50%);min-width:44px;min-height:44px;border:0;background:none;color:rgba(255,255,255,0.75);font-size:44px;line-height:1;cursor:pointer;';

      const nextLightboxButton = document.createElement('button');
      nextLightboxButton.type = 'button';
      nextLightboxButton.setAttribute('aria-label', 'Next image');
      nextLightboxButton.textContent = '›';
      nextLightboxButton.style.cssText =
        'position:absolute;right:8px;top:50%;transform:translateY(-50%);min-width:44px;min-height:44px;border:0;background:none;color:rgba(255,255,255,0.75);font-size:44px;line-height:1;cursor:pointer;';

      lightboxImage = document.createElement('img');
      lightboxImage.alt = 'The Paradox book';
      lightboxImage.draggable = false;
      lightboxImage.style.cssText =
        'max-width:min(92vw,1200px);max-height:84vh;object-fit:contain;border-radius:10px;user-select:none;';

      const dotsWrap = document.createElement('div');
      dotsWrap.style.cssText =
        'position:absolute;left:50%;bottom:16px;transform:translateX(-50%);display:flex;gap:8px;';

      lightboxDots = images.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `View image ${index + 1}`);
        dot.style.cssText =
          'width:10px;height:10px;border-radius:999px;border:0;cursor:pointer;min-width:20px;min-height:20px;background:rgba(255,255,255,0.4);';
        dot.addEventListener('click', (event) => {
          event.stopPropagation();
          goTo(index);
        });
        dotsWrap.appendChild(dot);
        return dot;
      });

      closeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        closeLightbox();
      });
      prevLightboxButton.addEventListener('click', (event) => {
        event.stopPropagation();
        goTo(currentIndex - 1);
      });
      nextLightboxButton.addEventListener('click', (event) => {
        event.stopPropagation();
        goTo(currentIndex + 1);
      });

      lightboxRoot.addEventListener('click', (event) => {
        if (event.target === lightboxRoot) closeLightbox();
      });

      lightboxRoot.addEventListener(
        'touchstart',
        (event) => {
          lightboxTouchStartX = event.changedTouches[0].clientX;
        },
        { passive: true }
      );
      lightboxRoot.addEventListener(
        'touchend',
        (event) => {
          const lightboxTouchEndX = event.changedTouches[0].clientX;
          const delta = lightboxTouchStartX - lightboxTouchEndX;
          if (Math.abs(delta) < 45) return;
          if (delta > 0) {
            goTo(currentIndex + 1);
          } else {
            goTo(currentIndex - 1);
          }
        },
        { passive: true }
      );

      document.addEventListener('keydown', (event) => {
        if (!lightboxOpen) return;
        if (event.key === 'Escape') {
          closeLightbox();
          return;
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(currentIndex - 1);
          return;
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(currentIndex + 1);
        }
      });

      lightboxPanel.appendChild(closeButton);
      lightboxPanel.appendChild(prevLightboxButton);
      lightboxPanel.appendChild(lightboxImage);
      lightboxPanel.appendChild(nextLightboxButton);
      lightboxPanel.appendChild(dotsWrap);
      lightboxRoot.appendChild(lightboxPanel);
      document.body.appendChild(lightboxRoot);
    };

    const applyState = () => {
      images.forEach((image, index) => {
        const isActive = index === currentIndex;
        image.classList.toggle('opacity-100', isActive);
        image.classList.toggle('opacity-0', !isActive);
      });

      dots.forEach((dot, index) => {
        const isActive = index === currentIndex;
        dot.classList.toggle('bg-primary', isActive);
        dot.classList.toggle('bg-foreground/30', !isActive);
      });

      thumbs.forEach((thumb, index) => {
        const isActive = index === currentIndex;
        thumb.classList.toggle('border-primary', isActive);
        thumb.classList.toggle('border-border', !isActive);
      });

      if (lightboxOpen) updateLightbox();
    };

    const goTo = (index) => {
      currentIndex = normalize(index);
      applyState();
    };

    prevButton?.addEventListener('click', (event) => {
      event.preventDefault();
      goTo(currentIndex - 1);
    });

    nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      goTo(currentIndex + 1);
    });

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        goTo(index);
      });
    });

    let touchStartX = 0;

    touchArea.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.changedTouches[0].clientX;
      },
      { passive: true }
    );

    touchArea.addEventListener(
      'touchend',
      (event) => {
        const touchEndX = event.changedTouches[0].clientX;
        const delta = touchStartX - touchEndX;

        if (Math.abs(delta) < 45) return;
        suppressFrameClick = true;
        window.setTimeout(() => {
          suppressFrameClick = false;
        }, 0);
        if (delta > 0) {
          goTo(currentIndex + 1);
        } else {
          goTo(currentIndex - 1);
        }
      },
      { passive: true }
    );

    buildLightbox();
    if (hasThumbs) {
      frame.style.cursor = 'zoom-in';
      frame.addEventListener('click', () => {
        if (suppressFrameClick) return;
        openLightbox();
      });
    }

    applyState();
  };

  const initGalleries = () => {
    const touchAreas = toArray(document.querySelectorAll('.touch-pan-y'));
    touchAreas.forEach(initGallery);
  };

  const initFaq = () => {
    const faqTitle = toArray(document.querySelectorAll('h3')).find(
      (heading) => heading.textContent.trim().toLowerCase() === 'faq'
    );

    if (!faqTitle) return;

    const faqContainer = faqTitle.parentElement;
    if (!faqContainer) return;

    const items = toArray(faqContainer.querySelectorAll(':scope > .border-b'));
    if (!items.length) return;

    let openedIndex = -1;

    const render = () => {
      items.forEach((item, index) => {
        const isOpen = index === openedIndex;
        const panel = item.querySelector('div.grid');
        const icon = item.querySelector('button svg');

        if (panel) {
          panel.classList.toggle('grid-rows-[1fr]', isOpen);
          panel.classList.toggle('opacity-100', isOpen);
          panel.classList.toggle('grid-rows-[0fr]', !isOpen);
          panel.classList.toggle('opacity-0', !isOpen);
        }

        if (icon) {
          icon.classList.toggle('rotate-180', isOpen);
        }
      });
    };

    items.forEach((item, index) => {
      const button = item.querySelector('button');
      if (!button) return;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        openedIndex = openedIndex === index ? -1 : index;
        render();
      });
    });

    render();
  };

  const findNewsletterForm = () =>
    toArray(document.querySelectorAll('form')).find((form) => form.querySelector('input[type="email"]'));

  const initStickyFeaturedButton = () => {
    const stickyButton = toArray(document.querySelectorAll('button')).find(
      (button) => /sign up to be featured/i.test(button.textContent)
    );

    if (!stickyButton) return;

    const stickyWrapper = stickyButton.parentElement;
    if (!stickyWrapper) return;

    const prefersReducedMotion =
      'matchMedia' in window && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    stickyWrapper.style.transition = prefersReducedMotion
      ? 'opacity 0.2s ease'
      : 'opacity 0.32s ease, transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)';
    stickyWrapper.style.willChange = 'opacity, transform';

    let isVisible = null;
    let rafId = 0;

    const applyVisibility = (nextVisible) => {
      if (isVisible === nextVisible) return;
      isVisible = nextVisible;

      stickyWrapper.style.pointerEvents = nextVisible ? 'auto' : 'none';
      stickyWrapper.style.opacity = nextVisible ? '1' : '0';
      stickyWrapper.style.transform = nextVisible ? 'translateY(0)' : 'translateY(100px)';
    };

    const updateVisibility = () => {
      applyVisibility(window.scrollY >= window.innerHeight);
    };

    const requestUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateVisibility();
      });
    };

    applyVisibility(false);
    updateVisibility();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
  };

  const initPopup = () => {
    const overlay = document.querySelector('[data-paradox-popup-overlay]');
    const layer = document.querySelector('[data-paradox-popup-layer]');
    const dialog = document.querySelector('[data-paradox-popup-dialog]');
    if (!overlay || !layer || !dialog) return null;

    const root = document.getElementById('root');
    const backersUrl = root ? (root.getAttribute('data-paradox-backers-url') || '').trim() : '';
    const closeButtons = toArray(layer.querySelectorAll('[data-paradox-popup-close]'));
    const popupForm = layer.querySelector('[data-paradox-popup-form]');
    const input = popupForm ? popupForm.querySelector('input[type="email"]') : null;
    const error = layer.querySelector('[data-paradox-popup-error]');
    const submit = popupForm ? popupForm.querySelector('button[type="submit"]') : null;
    const formBlock = layer.querySelector('[data-paradox-popup-form-block]');
    const successBlock = layer.querySelector('[data-paradox-popup-success]');
    const prefersReducedMotion =
      'matchMedia' in window && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animationDuration = prefersReducedMotion ? 1 : 280;

    let isOpen = false;
    let closeTimer = 0;

    overlay.style.opacity = '0';
    overlay.style.transition = prefersReducedMotion ? 'none' : 'opacity 0.24s ease';
    dialog.style.opacity = '0';
    dialog.style.transform = prefersReducedMotion ? 'none' : 'translateY(18px) scale(0.96)';
    dialog.style.transition = prefersReducedMotion
      ? 'none'
      : 'opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1), transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)';
    dialog.style.willChange = 'opacity, transform';
    if (submit && !submit.dataset.defaultText) {
      submit.dataset.defaultText = submit.textContent;
    }

    const resetState = () => {
      if (popupForm) popupForm.reset();
      if (error) {
        error.hidden = true;
        error.textContent = 'Please enter a valid email';
      }
      if (formBlock) formBlock.hidden = false;
      if (successBlock) successBlock.hidden = true;
      if (submit) {
        submit.disabled = false;
        submit.classList.remove('is-loading');
        submit.textContent = submit.dataset.defaultText || 'Join the Waitlist';
      }
    };

    const open = () => {
      if (isOpen) return;
      isOpen = true;

      resetState();
      window.clearTimeout(closeTimer);
      overlay.style.display = 'block';
      layer.style.display = 'flex';
      layer.setAttribute('aria-hidden', 'false');
      layer.style.pointerEvents = 'auto';
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
        dialog.style.opacity = '1';
        dialog.style.transform = 'translateY(0) scale(1)';
      });

      window.setTimeout(() => {
        const input = layer.querySelector('input[type="email"]');
        input?.focus();
      }, prefersReducedMotion ? 0 : 120);
    };

    const close = () => {
      if (!isOpen) return;
      isOpen = false;

      layer.style.pointerEvents = 'none';
      overlay.style.opacity = '0';
      dialog.style.opacity = '0';
      dialog.style.transform = prefersReducedMotion ? 'none' : 'translateY(18px) scale(0.96)';
      layer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (isOpen) return;
        overlay.style.display = 'none';
        layer.style.display = 'none';
      }, animationDuration);

      resetState();
    };

    overlay.addEventListener('click', close);
    closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        close();
      });
    });

    layer.addEventListener('click', (event) => {
      if (!dialog.contains(event.target)) {
        close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    popupForm?.addEventListener('submit', async (event) => {
      event.preventDefault();

      const submittedForm = event.target;
      const email = input ? input.value.trim() : '';

      if (!isValidEmail(email)) {
        if (error) {
          error.textContent = 'Please enter a valid email';
          error.hidden = false;
        }
        return;
      }

      if (!backersUrl) {
        if (error) {
          error.textContent = 'Backers URL is not configured';
          error.hidden = false;
        }
        return;
      }

      if (error) {
        error.hidden = true;
      }

      let oldSubmitText = '';
      if (submit) {
        submit.disabled = true;
        submit.classList.add('is-loading');
        oldSubmitText = submit.textContent;
        submit.textContent = 'Sending...';
      }

      const formData = new FormData(submittedForm);
      formData.set('email', email);

      try {
        const response = await fetch(`https://api.logistics.hungryminds.com/v1/shopify/backers/${backersUrl}`, {
          method: 'POST',
          body: formData,
        });
        const json = await response.json();

        if (json && json.success) {
          popupForm.reset();
          if (formBlock) formBlock.hidden = true;
          if (successBlock) successBlock.hidden = false;

          window.dataLayer.push({
            event: 'form_submitted',
            event_data: {
              status: 'success',
            }
          });
        } else if (error) {
          error.textContent = 'Failed to send. Please try again.';
          error.hidden = false;
        }
      } catch (_error) {
        if (error) {
          error.textContent = 'Failed to send. Please try again.';
          error.hidden = false;
        }
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.classList.remove('is-loading');
          submit.textContent = oldSubmitText || submit.dataset.defaultText || 'Join the Waitlist';
        }
      }
    });

    input?.addEventListener('input', () => {
      if (error && !error.hidden) {
        error.textContent = 'Please enter a valid email';
        error.hidden = true;
      }
    });

    return { open, close };
  };

  const initSignUpButtons = (newsletterForm, popupController) => {
    if (!newsletterForm && !popupController) return;

    const signUpButtons = toArray(document.querySelectorAll('button')).filter(
      (button) => /^sign up\b/i.test(button.textContent.trim())
    );

    signUpButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        if (popupController) {
          popupController.open();
          return;
        }

        if (!newsletterForm) return;

        newsletterForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => {
          const input = newsletterForm.querySelector('input[type="email"]');
          input?.focus();
        }, 400);
      });
    });
  };

  const initNewsletterForm = (newsletterForm) => {
    if (!newsletterForm) return;

    const root = document.getElementById('root');
    const backersUrl = root ? (root.getAttribute('data-paradox-backers-url') || '').trim() : '';
    const input = newsletterForm.querySelector('input[type="email"]');
    const submit = newsletterForm.querySelector('button[type="submit"]');

    if (!input || !submit) return;

    let error = newsletterForm.parentNode?.querySelector('[data-paradox-newsletter-error]');
    if (!error) {
      error = document.createElement('p');
      error.setAttribute('data-paradox-newsletter-error', '');
      error.className = 'error text-xs text-destructive mt-3 text-center';
      error.hidden = true;
      newsletterForm.parentNode?.insertBefore(error, newsletterForm.nextSibling);
    }

    let success = newsletterForm.parentNode?.querySelector('[data-paradox-newsletter-success]');
    if (!success) {
      success = document.createElement('p');
      success.setAttribute('data-paradox-newsletter-success', '');
      success.className = 'success text-primary font-sans font-medium text-center mt-3';
      success.textContent = 'Thank you for subscribing!';
      success.hidden = true;
      newsletterForm.parentNode?.insertBefore(success, error.nextSibling);
    }

    newsletterForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submittedForm = event.target;
      const email = input.value.trim();

      if (!isValidEmail(email)) {
        error.textContent = 'Please enter a valid email';
        error.hidden = false;
        return;
      }

      if (!backersUrl) {
        error.textContent = 'Backers URL is not configured';
        error.hidden = false;
        return;
      }

      error.hidden = true;
      success.hidden = true;

      const oldSubmitText = submit.textContent;
      submit.disabled = true;
      submit.classList.add('is-loading');
      submit.textContent = 'Sending...';

      const formData = new FormData(submittedForm);
      formData.set('email', email);

      try {
        const response = await fetch(`https://api.logistics.hungryminds.com/v1/shopify/backers/${backersUrl}`, {
          method: 'POST',
          body: formData,
        });
        const json = await response.json();

        if (json && json.success) {
          newsletterForm.reset();
          newsletterForm.style.display = 'none';
          success.hidden = false;
          window.dataLayer.push({
            event: 'form_submitted',
            event_data: {
              status: 'success',
            }
          });
        } else {
          error.textContent = 'Failed to send. Please try again.';
          error.hidden = false;
        }
      } catch (_error) {
        error.textContent = 'Failed to send. Please try again.';
        error.hidden = false;
      } finally {
        submit.disabled = false;
        submit.classList.remove('is-loading');
        submit.textContent = oldSubmitText;
      }
    });

    input.addEventListener('input', () => {
      if (!error.hidden) {
        error.textContent = 'Please enter a valid email';
        error.hidden = true;
      }
    });
  };

  const updateFooterYear = () => {
    const footerCopy = toArray(document.querySelectorAll('footer p')).find((element) =>
      element.textContent.includes('Think Again') && element.textContent.includes('©')
    );

    if (!footerCopy) return;
    footerCopy.textContent = footerCopy.textContent.replace(/©\s*\d{4}/, `© ${new Date().getFullYear()}`);
  };

  onReady(() => {
    initRevealAnimations();
    initGalleries();
    initFaq();
    initStickyFeaturedButton();

    const newsletterForm = findNewsletterForm();
    const popupController = initPopup();
    initSignUpButtons(newsletterForm, popupController);
    initNewsletterForm(newsletterForm);

    updateFooterYear();
  });
})();
